import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackLandingPage } from '../../../tracking';
import {
  FAA_LANDING_PAGE_MODULE,
  getVisibleLandingPageModules,
} from '../../../tracking/landing-page';
import type { FaaLandingPageModule } from '../../../tracking/landing-page';

interface UseLandingPageTrackingOptions {
  mixpanelHasInitialized: boolean;
  showCallToActions: boolean;
  showUltimateGuide: boolean;
  showSuburbSnapshot: boolean;
}

interface UseLandingPageTrackingResult {
  onModuleRendered: (module: FaaLandingPageModule) => void;
}

const useLandingPageTracking = ({
  mixpanelHasInitialized,
  showCallToActions,
  showUltimateGuide,
  showSuburbSnapshot,
}: UseLandingPageTrackingOptions): UseLandingPageTrackingResult => {
  const hasTrackedView = useRef(false);
  const [loadedModules, setLoadedModules] = useState<FaaLandingPageModule[]>(
    [],
  );

  const visibleModules = useMemo(
    () =>
      getVisibleLandingPageModules({
        hasContentCarouselGuides: showUltimateGuide,
        hasCtaDriverGetAppraisal: showCallToActions,
        hasCtaDriverTrackHome: showCallToActions,
        hasCtaDriverRecentlySold: showCallToActions,
        hasSuburbSnapshot: showSuburbSnapshot,
      }),
    [showUltimateGuide, showCallToActions, showSuburbSnapshot],
  );

  const onModuleRendered = useCallback((module: FaaLandingPageModule): void => {
    setLoadedModules((previousModules) => {
      if (previousModules.includes(module)) {
        return previousModules;
      }

      return [...previousModules, module];
    });
  }, []);

  useEffect(() => {
    if (showSuburbSnapshot) {
      onModuleRendered(FAA_LANDING_PAGE_MODULE.SUBURB_SNAPSHOT);
    }
  }, [showSuburbSnapshot, onModuleRendered]);

  useEffect(() => {
    const allVisibleModulesLoaded = visibleModules.every((module) =>
      loadedModules.includes(module),
    );

    if (
      mixpanelHasInitialized &&
      allVisibleModulesLoaded &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      trackLandingPage.trackViewLandingPage(visibleModules);
    }
  }, [mixpanelHasInitialized, visibleModules, loadedModules]);

  return { onModuleRendered };
};

export default useLandingPageTracking;
